/**
 * Typed timing policy for the browser session state machine.
 *
 * This module is the only owner of timing defaults. Composition roots parse
 * `import.meta.env` once and inject the resulting immutable object into the
 * gate/API helpers; event handlers never read deployment variables directly.
 */

export interface SessionTimingConfig {
  readonly probeTimeoutMs: number;
  readonly probeMaxAttempts: number;
  readonly probeRetryBaseMs: number;
  readonly probeRetryMaxMs: number;
  readonly probeRetryJitterRatio: number;
  readonly refreshTimeoutMs: number;
  readonly logoutTimeoutMs: number;
  readonly logoutRetryCount: number;
  readonly rateLimitFallbackSec: number;
  readonly visibilityReprobeAfterMs: number;
  readonly crossTabRefreshLeaseMs: number;
}

export const DEFAULT_SESSION_TIMINGS: Readonly<SessionTimingConfig> =
  Object.freeze({
    probeTimeoutMs: 8_000,
    probeMaxAttempts: 2,
    probeRetryBaseMs: 500,
    probeRetryMaxMs: 4_000,
    probeRetryJitterRatio: 0.2,
    refreshTimeoutMs: 8_000,
    logoutTimeoutMs: 8_000,
    logoutRetryCount: 1,
    rateLimitFallbackSec: 60,
    visibilityReprobeAfterMs: 60_000,
    crossTabRefreshLeaseMs: 12_000,
  });

export type SessionTimingEnvironment = Readonly<
  Record<string, string | undefined>
>;

type MutableSessionTimingConfig = {
  -readonly [Key in keyof SessionTimingConfig]: SessionTimingConfig[Key];
};

export class SessionConfigError extends Error {
  readonly field: keyof SessionTimingConfig;
  readonly envKey: string;

  constructor(
    field: keyof SessionTimingConfig,
    envKey: string,
    reason: string,
  ) {
    super(`Invalid ${envKey} (${field}): ${reason}`);
    this.name = "SessionConfigError";
    this.field = field;
    this.envKey = envKey;
  }
}

type NumericRule = "positive-integer" | "non-negative-integer" | "unit-ratio";

interface ConfigField {
  readonly field: keyof SessionTimingConfig;
  readonly envKey: string;
  readonly rule: NumericRule;
}

// Defaults own the field list; names retain the deployed VITE_SESSION_* contract.
const CONFIG_FIELDS: readonly ConfigField[] = (
  Object.keys(DEFAULT_SESSION_TIMINGS) as (keyof SessionTimingConfig)[]
).map((field) => ({
  field,
  envKey: `VITE_SESSION_${field.replace(/[A-Z]/gu, "_$&").toUpperCase()}`,
  rule:
    field === "probeRetryJitterRatio"
      ? "unit-ratio"
      : field === "logoutRetryCount"
        ? "non-negative-integer"
        : "positive-integer",
}));

function parseNumber(raw: string, config: ConfigField): number {
  if (raw.trim() === "") {
    throw new SessionConfigError(config.field, config.envKey, "value is empty");
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    throw new SessionConfigError(
      config.field,
      config.envKey,
      "value must be finite",
    );
  }

  switch (config.rule) {
    case "positive-integer":
      if (!Number.isInteger(value) || value <= 0) {
        throw new SessionConfigError(
          config.field,
          config.envKey,
          "value must be a positive integer",
        );
      }
      break;
    case "non-negative-integer":
      if (!Number.isInteger(value) || value < 0) {
        throw new SessionConfigError(
          config.field,
          config.envKey,
          "value must be a non-negative integer",
        );
      }
      break;
    case "unit-ratio":
      if (value < 0 || value > 1) {
        throw new SessionConfigError(
          config.field,
          config.envKey,
          "value must be between 0 and 1",
        );
      }
      break;
  }
  return value;
}

/** Parse deployment overrides into one fail-fast, immutable config object. */
export function parseSessionTimingConfig(
  env: SessionTimingEnvironment,
): Readonly<SessionTimingConfig> {
  const parsed: MutableSessionTimingConfig = { ...DEFAULT_SESSION_TIMINGS };

  for (const config of CONFIG_FIELDS) {
    const raw = env[config.envKey];
    if (raw !== undefined) {
      parsed[config.field] = parseNumber(raw, config);
    }
  }

  if (parsed.probeRetryMaxMs < parsed.probeRetryBaseMs) {
    throw new SessionConfigError(
      "probeRetryMaxMs",
      "VITE_SESSION_PROBE_RETRY_MAX_MS",
      "probeRetryMaxMs must be greater than or equal to probeRetryBaseMs",
    );
  }

  return Object.freeze(parsed);
}
